import { Box, Fab, Grid, IconButton, Typography } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { FieldArray } from "formik";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Center } from "../../../../components/center";
import { Form } from "../../../../components/form";
import { FormButton } from "../../../../components/form_button";
import { SelectInput } from "../../../../components/select_input";
import { TextInput } from "../../../../components/text_input";
import { IAsyncData, IFormProps } from "../../../../core/models";
import { isPending } from "../../../../core/redux";
import { fixAmount } from "../../../../core/utils";
import { IAppReduxState } from "../../../../redux/store";
import { IClient } from "../../../client/data/entities";
import { clientRedux } from "../../../client/ui/state/state";
import { IProduct } from "../../../product/data/entities";
import { productRedux } from "../../../product/ui/state/state";
import { calcCleanWeight, calcWastePercent } from "../../../product/utils";
import { ETransactionType } from "../../data/models";
import { IProductTransactionForm } from "../../data/product_buy_transaction_entity";
import {
  IProductSellBulkTransactionForm,
  productSellTransactionFormValidation,
} from "../../data/product_sell_transaction_entity";
import { calcTransactionFormSummary } from "../../utils";

interface IProps extends IFormProps<IProductSellBulkTransactionForm> {}

export const ProductSellTransactionForm: React.FC<IProps> = (props: IProps) => {
  const { branch, submitTitle } = props;
  const loading = isPending(branch);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(clientRedux.actions.getList());
    dispatch(productRedux.actions.getList());
  }, [dispatch]);
  const clientListBranch = useSelector<IAppReduxState, IAsyncData<IClient[]>>((state) => state.client.list);
  const productListBranch = useSelector<IAppReduxState, IAsyncData<IProduct[]>>((state) => state.product.list);

  const initialTransactionValues: IProductTransactionForm = {
    productId: "",
    amount: 0,
    cleanWeight: 0,
    pricePerKg: 0,
    dirtyWeight: 0,
    wastePercent: 0,
  };

  return (
    <Form<IProductSellBulkTransactionForm>
      {...props}
      initialValues={{
        clientId: "",
        productTransactions: [initialTransactionValues],
        paidAmount: 0,
        type: ETransactionType.PRODUCT_SELL,
      }}
      validationSchema={productSellTransactionFormValidation}
    >
      {({ values, setFieldValue }) => {
        return (
          <>
            <Grid container justify="center">
              <Typography variant="h5">Satış</Typography>
            </Grid>
            <Grid container>
              <Grid item sm={3}>
                <SelectInput<IClient>
                  value={values.clientId}
                  options={clientListBranch}
                  name={`clientId`}
                  label="Klient"
                  renderLabel={(a) => a.name}
                />
              </Grid>
            </Grid>

            <FieldArray name="productTransactions">
              {(arrayHelpers) => {
                const total = calcTransactionFormSummary(values.productTransactions);

                return (
                  <>
                    {values.productTransactions.map((transaction, index) => {
                      const transactionProduct = productListBranch.data?.find(
                        (product) => product.id === transaction.productId,
                      );
                      const income = fixAmount(transaction.pricePerKg - (transactionProduct?.price ?? 0));

                      return (
                        <Grid container key={index} spacing={1}>
                          <Grid item sm={2}>
                            <SelectInput<IProduct>
                              value={transaction.productId}
                              options={productListBranch}
                              name={`productTransactions.${index}.productId`}
                              label="Mal"
                              renderLabel={(a) => a.name}
                              onChange={(e) => {
                                const selectedProduct = productListBranch.data?.find(
                                  (product) => product.id === e.target.value,
                                );

                                if (selectedProduct) {
                                  setFieldValue(
                                    `productTransactions.${index}.wastePercent` as any,
                                    selectedProduct?.defaultWastePercent,
                                  );
                                }
                              }}
                            />
                          </Grid>

                          <Grid item sm={2}>
                            <TextInput
                              label="Çirkli çəki"
                              name={`productTransactions.${index}.dirtyWeight`}
                              type="number"
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setFieldValue(
                                  `productTransactions.${index}.cleanWeight` as any,
                                  calcCleanWeight({
                                    dirtyWeight: val,
                                    wastePercent: transaction.wastePercent,
                                  }),
                                );
                              }}
                              validate={() => {
                                let error;
                                const productId = transaction.productId;
                                const selectedProduct = productListBranch.data?.find(
                                  (product) => product.id === productId,
                                );
                                if (selectedProduct) {
                                  const totalDirtyWeight = selectedProduct.dirtyWeight;

                                  if (transaction.dirtyWeight > totalDirtyWeight) {
                                    error = `Anbarda ${totalDirtyWeight} kq ${selectedProduct.name} var. Bundan cox satmaq olmaz`;
                                  }
                                }

                                return error;
                              }}
                            />
                          </Grid>

                          <Grid item sm={2}>
                            <TextInput
                              label="Təmiz çəki"
                              type="number"
                              name={`productTransactions.${index}.cleanWeight`}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setFieldValue(
                                  `productTransactions.${index}.wastePercent` as any,
                                  calcWastePercent({
                                    cleanWeight: val,
                                    dirtyWeight: transaction.dirtyWeight,
                                  }),
                                );
                              }}
                            />
                          </Grid>

                          <Grid item sm={1}>
                            <TextInput
                              label="İtki faizi"
                              type="number"
                              name={`productTransactions.${index}.wastePercent`}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setFieldValue(
                                  `productTransactions.${index}.cleanWeight` as any,
                                  calcCleanWeight({
                                    wastePercent: val,
                                    dirtyWeight: transaction.dirtyWeight,
                                  }),
                                );
                              }}
                            />
                          </Grid>

                          <Grid item sm={1}>
                            <TextInput
                              label="Qiymət"
                              type="number"
                              name={`productTransactions.${index}.pricePerKg`}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);

                                setFieldValue(
                                  `productTransactions.${index}.amount` as any,
                                  fixAmount(transaction.cleanWeight * (val || 0)),
                                );
                              }}
                            />
                          </Grid>

                          <Grid item sm={1}>
                            <TextInput
                              disabled
                              label="Qazanc"
                              type="number"
                              name="income"
                              value={income}
                              error={income < 0}
                              helperText={income < 0 ? "Anbardan asagi qiymet" : ""}
                            />
                          </Grid>

                          <Grid item sm={2}>
                            <TextInput
                              type="number"
                              label="Cəm qiymət"
                              name={`productTransactions.${index}.amount`}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);

                                setFieldValue(
                                  `productTransactions.${index}.pricePerKg` as any,
                                  fixAmount((val || 0) / transaction.cleanWeight),
                                );
                              }}
                            />
                          </Grid>

                          <Grid item xs={1}>
                            {index !== 0 && (
                              <Center>
                                <IconButton aria-label="delete" onClick={() => arrayHelpers.remove(index)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Center>
                            )}
                          </Grid>
                        </Grid>
                      );
                    })}

                    <Grid container justify="flex-end">
                      <Grid item>
                        <Typography variant="h5" align="right">
                          Cəm: {total} AZN
                        </Typography>
                      </Grid>
                      <Grid item sm={1}></Grid>
                    </Grid>

                    <Grid container>
                      <Box p={3}>
                        <Fab size="small" color="secondary" onClick={() => arrayHelpers.push(initialTransactionValues)}>
                          <AddIcon />
                        </Fab>
                      </Box>
                    </Grid>
                  </>
                );
              }}
            </FieldArray>

            <Grid container>
              <Grid item sm={2}>
                <TextInput label="Ödənilən məbləğ" name="paidAmount" type="number" />
              </Grid>
            </Grid>

            <Box mb={2}></Box>

            <Grid container>
              <Grid item sm={12} md={3}>
                <FormButton label={submitTitle} loading={loading} />
              </Grid>
            </Grid>
          </>
        );
      }}
    </Form>
  );
};
